import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from 'reactstrap';
import { createLink, deleteLink, getLinks, Link, LinkInput, updateOrder, updateLink } from 'features/contents/linksAPI';
import List from 'components/Sortable/List';
import { useAlertError } from 'hooks';
import AlertError from 'components/AlertError';
import { getErrorMessage } from 'utils/errors';
import { Link as RRDLink } from 'react-router-dom';

interface LinksProps {
  section?: string;
  title?: string;
}

export default function Links({ section = 'home', title = 'Lista de Enlaces' }: LinksProps) {
  const [links, setLinks] = useState<Link[]>([]);
  const [currentLink, setCurrentLink] = useState<LinkInput>();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState<File | null>(null);

  const [showError, messageError, detailsError, callbackError, alert] = useAlertError();

  useEffect(() => {
    getLinks(section)
      .then(links => {
        if (!links || !Array.isArray(links)) {
          alert(
            'No se pudo recuperar los enlaces. Por favor, intente nuevamente más tarde',
            () => {
              setLinks([]);
              setCurrentLink(undefined);
              setIsModalOpen(false);
            },
          );
          return;
        }
        setLinks(links);
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo recuperar los enlaces. Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
          () => {
            setLinks([]);
            setCurrentLink(undefined);
            setIsModalOpen(false);
          },
        );
      });
  }, [section]);

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentLink(undefined);
    setPdfFile(null);
  };

  const handleEditLinkClick = (id: number) => {
    const found = links.find(l => l.id === id);
    if (!found) {
      setCurrentLink(undefined);
      return;
    }
    setCurrentLink({ ...found });
    setPdfFile(null);
    setIsModalOpen(true);
  };

  const handleAddLinkClick = () => {
    setCurrentLink({
      id: -1,
      name: '',
      description: '',
      url: '',
      order: links.length + 1,
      section,
    });
    setPdfFile(null);
    setIsModalOpen(true);
  };

  const handleRemoveLinkClick = (id: number) => {
    deleteLink(id).then(result => {
      if (!result) return;
      setLinks(links.filter(l => l.id !== id));
    });
  };

  const handleSaveLinkClick = () => {
    if (!currentLink) return;

    const payload: LinkInput = { ...currentLink, section, pdf: pdfFile };

    if (payload.id === -1) {
      createLink(payload)
        .then(result => {
          if (!result) {
            alert('No se pudo crear el enlace. Por favor, intente nuevamente más tarde', closeModal);
            return;
          }
          setLinks([...links, result]);
          closeModal();
        })
        .catch(err => {
          console.error(err);
          alert(
            'No se pudo crear el enlace. Por favor, intente nuevamente más tarde',
            getErrorMessage(err),
            closeModal,
          );
        });
      return;
    }

    updateLink(payload)
      .then(result => {
        if (!result) {
          alert('No se pudo actualizar el enlace. Por favor, intente nuevamente más tarde', closeModal);
          return;
        }
        const idx = links.findIndex(l => l.id === payload.id);
        const newLinks = [...links];
        newLinks[idx] = result;
        setLinks(newLinks);
        closeModal();
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo actualizar el enlace. Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
          closeModal,
        );
      });
  };

  const parseLink = (link: Link) => (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-evenly', alignItems: 'center' }}>
      <div style={{ padding: '.25rem', flexGrow: 1 }}>
        <div>
          <strong>{ link.name }</strong>{ link.description ? `: ${ link.description }` : '' }<br />
          { link.url }
        </div>
      </div>
      <Button onClick={ () => handleEditLinkClick(link.id) } color="primary" size="sm" outline>
        <i className="fa fa-edit"></i>
      </Button>
      <Button onClick={ () => handleRemoveLinkClick(link.id) } color="warning" size="sm" outline>
        <i className="fa fa-trash-alt"></i>
      </Button>
    </div>
  );

  const handleLinkContentChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!currentLink) return;
    setCurrentLink({ ...currentLink, [event.target.name]: event.target.value });
  };

  const handlePdfChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setPdfFile(file);
  };

  const handleLinkOrderChange = (newLinks: Link[]) => {
    const newOrder: Record<number, number> = {};
    for (let i = 0; i < newLinks.length; i++) {
      newOrder[i + 1] = newLinks[i].id;
    }
    updateOrder(newOrder, section)
      .then(result => {
        if (!result) {
          alert('No se pudo actualizar el orden de los enlaces. Por favor, intente nuevamente más tarde');
        }
        setLinks(newLinks);
      })
      .catch(err => {
        console.error(err);
        alert(
          'No se pudo actualizar el orden de los enlaces. Por favor, intente nuevamente más tarde',
          getErrorMessage(err),
        );
      });
  };

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body" />
        </Container>
      </div>
      <Container className="mt--7" fluid>
        <Row>
          <Col>
            <Card className="shadow">
              <CardHeader className="border-0">
                <Row className="align-items-center">
                  <Col md='8'>
                    <h3 className="mb-0">{ title }</h3>
                  </Col>
                  <Col md='4' className='text-right'>
                    <Button color='primary' className='btn btn-md' onClick={ handleAddLinkClick }>
                      Agregar enlace
                    </Button>
                    <RRDLink to="/admin/contenidos">
                      <Button color='secondary' className='btn btn-md'>Volver</Button>
                    </RRDLink>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {
                  links.length > 0 && (
                    <List<Link> items={ links } setItems={ handleLinkOrderChange } parser={ parseLink } />
                  )
                }
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={ isModalOpen } centered toggle={ closeModal }>
        <ModalHeader toggle={ closeModal }>Editar Enlace</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Texto</Label>
              <Input type="text" name="name" onChange={ handleLinkContentChange } value={ currentLink?.name || '' } />
            </FormGroup>
            <FormGroup>
              <Label>Descripción</Label>
              <Input
                type="textarea"
                name="description"
                onChange={ handleLinkContentChange }
                value={ currentLink?.description || '' }
                rows={5}
              />
            </FormGroup>
            <FormGroup>
              <Label>URL</Label>
              <Input type="url" name="url" onChange={ handleLinkContentChange } value={ currentLink?.url || '' } />
            </FormGroup>
            <FormGroup>
              <Label>Subir PDF (opcional)</Label>
              <Input type="file" accept="application/pdf" onChange={ handlePdfChange } />
              {
                pdfFile && (
                  <small className="text-muted">Archivo seleccionado: { pdfFile.name }</small>
                )
              }
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button onClick={ handleSaveLinkClick } color="primary">Guardar</Button>
          <Button onClick={ closeModal }>Cancelar</Button>
        </ModalFooter>
      </Modal>
      <AlertError isOpen={ showError } message={ messageError } details={ detailsError } onClose={ callbackError } />
    </>
  );
}
